/**
 * Client-side feature flag keys.
 *
 * These MUST match the `clientVisible: true` entries in `Config/feature-catalog.json`. The catalog
 * is authoritative; this union exists so a typo is a compile error rather than a permanently-false
 * flag. `FeatureCatalogClientContractTests` fails the build if the two drift apart.
 *
 * Flags here drive VISIBILITY ONLY. Every gated endpoint enforces its own flag server-side, so a
 * user who forces one of these on gains a visible link and a 404, never access.
 */
export type FeatureKey =
  | 'Exceptions.ExpiryExtension.Enabled'
  | 'Requests.AdminConsent.Production'
  | 'Requests.AdminConsent.Fdpo'
  | 'Assistant.Guidance'
  | 'Assistant.HomeExperience'
  | 'Assistant.Collect'
  | 'Requests.Exceptions.BulkUpload'
  | 'Requests.Approvals.ManualStatusOverride'
  | 'Requests.ChildRequests'
  | 'Containment.StandardizedRequests'
  | 'IdentityTools.OnboardingBuddy.MockDirectory'
  | 'WorkItems.ConnectorIdEditable'
  | 'WorkItems.TriageWorkspace'
  | 'Reports.Analytics'
  | 'Reports.ExecutiveOverview'
  | 'Reports.TriageLagTrend'
  | 'Reports.TemplateAdoption'
  | 'Reports.Icm'
  | 'Reports.KrCounts'
  | 'Reports.AccountActivity'
  | 'Reports.AccountActivity.AuthenticationMethodEvidence'
  | 'Reports.FedSnow'
  | 'Reports.HotSpots'
  | 'Reports.CrossSurfaceHotSpots'
  | 'Notifications.AgingDigest'
  | 'Reports.Routing'
  | 'Reports.Exceptions'
  | 'Reports.OutcomeMix'
  | 'Reports.DataQuality'
  | 'Reports.TriageTiming'
  | 'Reports.DeclinedDetail'
  | 'Reports.RejectionReasons'
  | 'Reports.ApprovalPerformance'
  | 'Reports.WorkflowHealth'
  | 'Reports.ExceptionExpiry'
  | 'Intake.TaxonomyMapping'
  | 'Reports.DeflectionEffectiveness'
  | 'Reports.InactiveUsers'
  | 'Reports.ElevatedAccess'
  | 'Reports.PainMeter'
  | 'Reports.ReliefEffectiveness'
  | 'Reports.TriageAging'
  | 'Reports.Rollups'
  | 'Reports.IntakeFunnel'
  | 'IdentityTools.PimSync.Management'
  | 'IdentityTools.OnboardingBuddy'
  | 'IdentityTools.Totp.Management'
  | 'IdentityTools.PartnerGovernance'
  | 'IdentityTools.PartnerGovernance.ActivityLog'
  | 'IdentityTools.PartnerGovernance.ApplicationScopes'
  | 'IdentityTools.PartnerGovernance.RetirementInsights'
  | 'IdentityTools.PartnerGovernance.UnconfiguredGuests'
  | 'IdentityTools.IpAnchor'
  | 'IdentityTools.IpAnchorWrites'
  | 'IdentityTools.HrAccountLookup.Restricted'
  | 'IdentityTools.CoreIdHrAccountLookup'
  | 'IdentityTools.Helpdesk'
  | 'IdentityTools.Harvest'
  | 'IdentityTools.Clearance'
  | 'IdentityTools.MsApproveOperations'
  | 'IdentityTools.ChangeRadar'
  | 'IdentityTools.ConditionalAccessReadiness'
  | 'IdentityTools.ConditionalAccessInvestigation'
  | 'IdentityTools.AttestationCentral'
  | 'IdentityTools.AttestationCentral.UamiDashboard'
  | 'IdentityTools.AttestationCentral.CustomRoleDashboard'
  | 'Operations.SreAgentRead';

/** Effective flag values as returned by `GET /api/features`. */
export type FeatureFlagMap = Partial<Record<FeatureKey, boolean>>;

/**
 * Absent means off. A flag that has not loaded yet, or that the server declined to emit, must read
 * as disabled — the safe direction for a capability gate, and it matches the server default.
 */
export const isFeatureEnabled = (flags: FeatureFlagMap, key: FeatureKey): boolean => flags[key] === true;
